import {
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { FeatureAutocompleteSelect } from "@/common/components/Autocomplete/FeatureAutocompleteSelect";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import RuleAttribute from "@/common/components/Rule/RuleAttribute";
import RuleMetric from "@/common/components/Rule/RuleMetric";
import RuleType from "@/common/components/Rule/RuleType";
import { useAttributeNames } from "@/common/data/useAttributeNames";
import { useDebounce } from "@/common/hooks/useDebounce";
import useCompanyAttributesData from "@/company/data/useCompanyAttributesData";
import { AttributeField } from "@reflag/shared/attributeFilter";
import { CompaniesColumnState } from "@reflag/shared/companyAPI";
import { SEARCH_DEBOUNCE_MS } from "@reflag/shared/constants";
import {
  BaseCompanyAttributeFilterSchema,
  BaseCompanyFeatureMetricFilterSchema,
  type FeatureMetric,
  featureMetrics,
  UserAttributeFilterSchema,
} from "@reflag/shared/filter";
import { ColumnState } from "@reflag/shared/types/columns";
import { UsersColumnState } from "@reflag/shared/userAPI";
import { z } from "@reflag/shared/utils/zod";

const formSchema = z.discriminatedUnion("type", [
  BaseCompanyAttributeFilterSchema.omit({
    operator: true,
    values: true,
  }),
  BaseCompanyFeatureMetricFilterSchema.omit({
    operator: true,
    values: true,
  }),
  UserAttributeFilterSchema.omit({
    operator: true,
    values: true,
  }),
]);

type FormValues = z.infer<typeof formSchema>;

type AttributeFieldsProps = {
  prefix: "attributes";
  columnStates: (CompaniesColumnState | UsersColumnState)[];
  attributes: AttributeField[];
  isLoading: boolean;
};
function AttributeFields({
  columnStates,
  prefix,
  attributes,
  isLoading,
}: AttributeFieldsProps) {
  const availableAttributes = useMemo(() => {
    return attributes
      ? attributes.filter(
          (a) => !columnStates?.find((c) => c.id === `${prefix}.${a.key}`),
        )
      : [];
  }, [attributes, columnStates, prefix]);

  return (
    <>
      <ManagedFormControl
        name="field"
        render={({ field }) => (
          <RuleAttribute
            {...field}
            attributes={availableAttributes}
            isLoading={isLoading}
          />
        )}
      />
    </>
  );
}

type CompanyAttributeFieldsProps = {
  columnStates: (CompaniesColumnState | UsersColumnState)[];
};

export function CompanyAttributeFields({
  columnStates,
}: CompanyAttributeFieldsProps) {
  const field = useWatch<FormValues>({ name: "field" });
  const debouncedField = useDebounce(field, SEARCH_DEBOUNCE_MS);
  const { data: attributesRaw = [], isLoading } = useCompanyAttributesData(
    true,
    typeof debouncedField === "string" ? debouncedField : undefined,
  );
  // only list custom attributes
  const attributes = attributesRaw.filter((i) => !i.system);
  return (
    <AttributeFields
      attributes={attributes}
      columnStates={columnStates}
      isLoading={isLoading}
      prefix="attributes"
    />
  );
}

type UserAttributeFieldsProps = {
  columnStates: (CompaniesColumnState | UsersColumnState)[];
};
function UserAttributeFields({ columnStates }: UserAttributeFieldsProps) {
  const field = useWatch<FormValues>({ name: "field" });
  const debouncedField = useDebounce(field, SEARCH_DEBOUNCE_MS);
  const { attributes: attributesRaw = [], isLoading } = useAttributeNames(
    "feature-targeting-context-user",
    typeof debouncedField === "string" ? debouncedField : undefined,
  );
  // only list custom attributes
  const attributes = attributesRaw.filter((i) => !i.system);

  return (
    <AttributeFields
      attributes={attributes}
      columnStates={columnStates}
      isLoading={isLoading}
      prefix="attributes"
    />
  );
}

type FeatureMetricFieldsProps = {
  allowedFeatureMetrics?: readonly FeatureMetric[];
  columnStates: ColumnState[];
};
function FeatureMetricFields({
  allowedFeatureMetrics,
  columnStates,
}: FeatureMetricFieldsProps) {
  const selectedMetrics = useMemo(() => {
    return (
      columnStates?.reduce<Record<string, string[]>>((acc, columnState) => {
        const [key, id, metric] = columnState.id.split(".");
        if (key !== "featureMetrics") return acc;
        return {
          ...acc,
          [id]: [...(acc[id] ?? []), metric],
        };
      }, {}) ?? {}
    );
  }, [columnStates]);

  const selectedFeatureId = useWatch<FormValues>({ name: "featureId" });

  const availableMetrics = useMemo(() => {
    const metrics = allowedFeatureMetrics ?? featureMetrics;
    if (typeof selectedFeatureId !== "string") return metrics;
    return metrics.filter(
      (metric) => !selectedMetrics[selectedFeatureId]?.includes(metric),
    );
  }, [allowedFeatureMetrics, selectedFeatureId, selectedMetrics]);

  return (
    <>
      <ManagedFormControl
        name="featureId"
        render={({ field }) => (
          <FeatureAutocompleteSelect {...field} valueAsId />
        )}
      />
      <ManagedFormControl
        name="metric"
        render={({ field }) => (
          <RuleMetric
            {...field}
            availableMetrics={availableMetrics}
            emptyPlaceholder="No more metrics available"
          />
        )}
      />
    </>
  );
}

type Props<TColumnState extends ColumnState<any>> = {
  columnStates: TColumnState[];
  isDisabled?: boolean;
  setColumnStates: (nextColumnStates: TColumnState[]) => void;
  allowedFeatureMetrics?: readonly FeatureMetric[];
  allowedTypes: ("companyAttribute" | "featureMetric" | "userAttribute")[];
};

export const AddColumnPopover = <TColumnState extends ColumnState<any>>({
  columnStates,
  isDisabled,
  setColumnStates,
  allowedFeatureMetrics,
  allowedTypes,
}: Props<TColumnState>) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      type: allowedTypes[0],
      field: "",
    },
  });

  const columnType = useWatch({
    control: form.control,
    name: "type",
  });

  const onSubmit = form.handleSubmit((data) => {
    switch (data.type) {
      case "companyAttribute":
        setColumnStates([
          ...columnStates,
          {
            id: `attributes.${data.field}`,
            shown: true,
          } as TColumnState,
        ]);
        form.reset({ type: "companyAttribute", field: "" });
        break;
      case "userAttribute":
        setColumnStates([
          ...columnStates,
          {
            id: `attributes.${data.field}`,
            shown: true,
          } as TColumnState,
        ]);
        form.reset({ type: "userAttribute", field: "" });
        break;
      case "featureMetric":
        setColumnStates([
          ...columnStates,
          {
            id: `featureMetrics.${data.featureId}.${data.metric}`,
            shown: true,
          } as TColumnState,
        ]);
        form.reset({
          type: "featureMetric",
          featureId: undefined,
          metric: undefined,
        });
        break;
      default:
        // This should never happen due to the form schema validation
        break;
    }
  });

  return (
    <Popover closeOnBlur={false} placement="left-start">
      <PopoverTrigger>
        <Button
          fontWeight="normal"
          isDisabled={isDisabled}
          size="sm"
          variant="outline"
        >
          Add column
        </Button>
      </PopoverTrigger>
      <PopoverContent maxW="64">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverBody p={5}>
          <FormProvider {...form}>
            <form onSubmit={onSubmit}>
              <Flex align="flex-start" direction="column" gap={2}>
                <Text color="dimmed" mb={2}>
                  Add column
                </Text>
                <ManagedFormControl
                  name="type"
                  render={({ field }) => (
                    <RuleType {...field} allowed={allowedTypes} />
                  )}
                />
                {columnType === "companyAttribute" ? (
                  <CompanyAttributeFields columnStates={columnStates} />
                ) : columnType === "userAttribute" ? (
                  <UserAttributeFields columnStates={columnStates} />
                ) : columnType === "featureMetric" ? (
                  <FeatureMetricFields
                    allowedFeatureMetrics={allowedFeatureMetrics}
                    columnStates={columnStates}
                  />
                ) : null}
                <FormSubmit loadingText="Adding..." mt={2} size="sm">
                  Add
                </FormSubmit>
              </Flex>
            </form>
          </FormProvider>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
